"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Button from "../../../components/Button";
import FormInput from "../../../components/FormInput";
import ImageDropzone from "../../../components/ImageDropzone";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Dropdown from "../../../components/Dropdown";
import styles from "../css/creditApplication.module.css";
import api from "../../../api";

const CreditApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const creditFlow = location?.state?.creditFlow;
  const isReviewMode = creditFlow?.mode === "review";
  const creditStatus = String(creditFlow?.status || "").toLowerCase();
  const isReadOnly = isReviewMode && ["awaiting", "pending", "decision"].includes(creditStatus);
  const canDownloadForVetting = isReviewMode && creditStatus === "awaiting";

  const savedData = location?.state?.formData || {};

  const steps = useMemo(
    () => [
      { key: "personal", label: "Personal" },
      { key: "contact", label: "Contact" },
      { key: "employment", label: "Employment" },
      { key: "financial", label: "Financial" },
      { key: "consent", label: "Consent" },
    ],
    []
  );

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    fullName: savedData.fullName || `${savedData.firstName || ""} ${savedData.lastName || ""}`.trim(),
    idNumber: savedData.idNumber || "",
    dob: savedData.dob || "",
    gender: savedData.gender || "",
    idCopy: savedData.idCopy || null,
    phone: savedData.phone || savedData.mobile || "",
    email: savedData.email || "",
    addressLine1: savedData.addressLine1 || savedData.streetAddress || "",
    addressLine2: savedData.addressLine2 || savedData.city || "",
    employer: savedData.employer || savedData.employerName || "",
    incomeMonthly: savedData.incomeMonthly || savedData.monthlyIncome || "",
    expensesMonthly: savedData.expensesMonthly || "",
    consent: Boolean(savedData.consent),
  });

  const parseSaIdDob = (id) => {
    if (!/^[0-9]{13}$/.test(id)) return "";
    const yy = parseInt(id.slice(0, 2), 10);
    const mm = parseInt(id.slice(2, 4), 10);
    const dd = parseInt(id.slice(4, 6), 10);
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";

    const currentYear = new Date().getFullYear() % 100;
    const fullYear = yy > currentYear ? 1900 + yy : 2000 + yy;
    const iso = `${fullYear}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
    return iso;
  };

  const handleIdNumberChange = (e) => {
    if (isReadOnly) return;
    const { value } = e.target;
    const maybeDob = parseSaIdDob(value);
    setForm((prev) => ({
      ...prev,
      idNumber: value,
      dob: maybeDob || prev.dob,
    }));
  };

  const onChange = (e) => {
    if (isReadOnly) return;
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    if (type === "file") {
      setForm((prev) => ({ ...prev, [name]: files?.[0] ?? null }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canGoBack = activeStep > 0 || isReviewMode;
  const canGoNext = activeStep < steps.length - 1;

  const goBack = () => {
    if (!canGoBack) return;
    if (activeStep === 0 && isReviewMode) {
      navigate("/credits/debtors");
      return;
    }
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const goNext = () => {
    if (!canGoNext) return;
    if (activeStep === 0) {
      navigate("/credits/contact", {
        state: {
          creditFlow,
          formData: {
            ...savedData,
            fullName: form.fullName,
            firstName: savedData.firstName,
            lastName: savedData.lastName,
            idNumber: form.idNumber,
            dob: form.dob,
            gender: form.gender,
            idCopy: form.idCopy,
            idCopyUrl: savedData.idCopyUrl || "",
          },
        },
      });
      return;
    }
    setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    navigate("/");
  };

  const handleDownloadForVetting = async () => {
    const applicationId = creditFlow?.applicationId;
    if (applicationId) {
      try {
        await api.patch(`/api/public/credit-applications/${applicationId}/status`, {
          status: "decision",
        });
      } catch (err) {
        console.error("Failed to update status to decision:", err);
        alert(err?.response?.data?.error || err?.message || "Failed to update status");
        return;
      }
    }

    const merged = {
      ...savedData,
      fullName: form.fullName,
      idNumber: form.idNumber,
      dob: form.dob,
      gender: form.gender,
    };

    const brandLavender = "#E4D6F6";
    const darkText = "#111827";
    const mutedText = "#6b7280";

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(brandLavender);
    doc.rect(0, 0, pageWidth, 64, "F");
    doc.setTextColor(darkText);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("MediIntel - Credit Application (Vetting)", 40, 40);

    doc.setTextColor(darkText);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const generatedAt = new Date().toLocaleString();
    doc.text(`Generated: ${generatedAt}`, 40, 86);
    if (creditFlow?.applicationId) {
      doc.text(`Application ID: ${creditFlow.applicationId}`, 40, 102);
    }
    if (creditFlow?.status) {
      doc.text(`Status: ${String(creditFlow.status)}`, 40, 118);
    }

    const addSection = (title, rows, startY) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(darkText);
      doc.text(title, 40, startY);

      autoTable(doc, {
        startY: startY + 8,
        head: [["Field", "Value"]],
        body: rows.map(([k, v]) => [k, v ?? ""]),
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 9,
          textColor: darkText,
          cellPadding: 6,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: brandLavender,
          textColor: darkText,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [243, 244, 246],
        },
        columnStyles: {
          0: { cellWidth: 170 },
          1: { cellWidth: "auto" },
        },
        margin: { left: 40, right: 40 },
      });

      const nextY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 22 : startY + 60;
      return nextY;
    };

    let y = 150;

    y = addSection(
      "Personal",
      [
        ["Full Name", merged.fullName || ""],
        ["First Name", merged.firstName || ""],
        ["Last Name", merged.lastName || ""],
        ["ID Number", merged.idNumber || ""],
        ["Date of Birth", merged.dob || ""],
        ["Gender", merged.gender || ""],
      ],
      y
    );

    y = addSection(
      "Contact",
      [
        ["Mobile", merged.mobile || ""],
        ["Email", merged.email || ""],
        ["Street Address", merged.streetAddress || ""],
        ["City", merged.city || ""],
        ["Province", merged.province || ""],
        ["Postal Code", merged.postalCode || ""],
      ],
      y
    );

    y = addSection(
      "Employment",
      [
        ["Employment Status", merged.employmentStatus || ""],
        ["Employer Name", merged.employerName || ""],
        ["Monthly Income", merged.monthlyIncome || ""],
        ["Pay Frequency", merged.payFrequency || ""],
      ],
      y
    );

    const normalizedExpenses = Array.isArray(merged.expenses) ? merged.expenses : [];
    const expensesValue =
      normalizedExpenses.length > 0
        ? normalizedExpenses
            .map((e) => `${String(e?.name || "").trim()}: R${String(e?.amount ?? "").trim()}`)
            .filter(Boolean)
            .join("\n")
        : "";
    const totalExpenses = normalizedExpenses.reduce((sum, expense) => {
      const rawAmount = String(expense?.amount ?? "").replace(/[^0-9.-]/g, "");
      const numericAmount = Number(rawAmount);
      return sum + (Number.isFinite(numericAmount) ? numericAmount : 0);
    }, 0);

    y = addSection(
      "Financial",
      [
        ["Has Existing Credit", merged.hasExistingCredit ? "Yes" : "No"],
        ["Preferred Payment Method", merged.preferredPaymentMethod || ""],
        ["Expenses", expensesValue],
        ["Total Expenses", `R${totalExpenses.toFixed(2)}`],
      ],
      y
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(mutedText);
    doc.text(
      "",
      40,
      Math.min(y, doc.internal.pageSize.getHeight() - 40)
    );

    const filenameId = String(merged.idNumber || "application").replace(/[^0-9a-zA-Z_-]/g, "");
    doc.save(`mediintel-credit-vetting-${filenameId}.pdf`);

    navigate("/credits/debtors");
  };

  return (
    <div className={styles.page}>
      <Navbar backTo="/" hideBack={isReviewMode} />

      <main className={styles.main}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Credit Application</h1>
          <p className={styles.subtitle}>
            Complete the form below to apply for purchase-on-credit at your pharmacy.
          </p>
        </div>

        <div className={styles.stepper} aria-label="Application steps">
          {steps.map((s, idx) => {
            const isActive = idx === activeStep;
            const isDone = idx < activeStep;
            return (
              <div key={s.key} className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${isActive ? styles.stepActive : ""} ${
                    isDone ? styles.stepDone : ""
                  }`}
                >
                  {idx + 1}
                </div>
                <div className={styles.stepLabel}>{s.label}</div>
                {idx < steps.length - 1 ? <div className={styles.stepLine} aria-hidden /> : null}
              </div>
            );
          })}
        </div>

        <form className={styles.card} onSubmit={onSubmit}>
          {activeStep === 0 ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="2" />
                    <path d="M20 20a8 8 0 10-16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div className={styles.sectionTitle}>Personal Information</div>
              </div>

              <div className={`${styles.grid} ${styles.personalGrid}`}>
                <FormInput
                  label="Full Name"
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  placeholder="Full name"
                  className={styles.personalFullName}
                  disabled={isReadOnly}
                />

                <FormInput
                  label="SA ID Number"
                  name="idNumber"
                  value={form.idNumber}
                  onChange={handleIdNumberChange}
                  placeholder="ID number"
                  className={styles.personalIdNumber}
                  disabled={isReadOnly}
                />

                <FormInput
                  label="Date of Birth"
                  type="date"
                  name="dob"
                  value={form.dob}
                  onChange={onChange}
                  disabled
                  className={styles.personalDob}
                />

                <Dropdown
                  label="Gender"
                  name="gender"
                  options={["Male", "Female"]}
                  value={form.gender}
                  onChange={(val) => {
                    if (isReadOnly) return;
                    setForm((prev) => ({ ...prev, gender: val }));
                  }}
                  placeholder="Select gender"
                  className={styles.personalGender}
                  disabled={isReadOnly}
                />

                <div className={`${styles.dropzoneWrap} ${styles.personalDropzone}`}>
                  <label className={styles.fieldLabel}>Upload a copy of your ID</label>
                  <ImageDropzone
                    id="idCopy"
                    name="idCopy"
                    accept="image/png,image/jpeg"
                    value={form.idCopy}
                    viewUrl={savedData.idCopyUrl || ""}
                    onChange={onChange}
                    placeholder="Drop files here"
                    helper="Supported format: PNG, JPG"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </section>
          ) : null}

          {activeStep === 1 ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16v16H4V4z" stroke="currentColor" strokeWidth="2" />
                    <path d="M4 8h16" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 4v16" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div className={styles.sectionTitle}>Contact</div>
              </div>

              <div className={styles.grid}>
                <FormInput
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Phone"
                  disabled={isReadOnly}
                />
                <FormInput
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="Email"
                  disabled={isReadOnly}
                />
                <FormInput
                  label="Address Line 1"
                  name="addressLine1"
                  value={form.addressLine1}
                  onChange={onChange}
                  placeholder="Address"
                  disabled={isReadOnly}
                />
                <FormInput
                  label="Address Line 2"
                  name="addressLine2"
                  value={form.addressLine2}
                  onChange={onChange}
                  placeholder="Address (optional)"
                  disabled={isReadOnly}
                />
              </div>
            </section>
          ) : null}

          {activeStep === 2 ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 7h18v13H3V7z" stroke="currentColor" strokeWidth="2" />
                    <path d="M7 7V4h10v3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div className={styles.sectionTitle}>Employment</div>
              </div>

              <div className={styles.grid}>
                <FormInput
                  label="Employer"
                  name="employer"
                  value={form.employer}
                  onChange={onChange}
                  placeholder="Employer"
                  disabled={isReadOnly}
                />
              </div>
            </section>
          ) : null}

          {activeStep === 3 ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1v22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M17 5H9.5a3.5 3.5 0 000 7H14a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div className={styles.sectionTitle}>Financial</div>
              </div>

              <div className={styles.grid}>
                <FormInput
                  label="Monthly Income"
                  name="incomeMonthly"
                  type="number"
                  value={form.incomeMonthly}
                  onChange={onChange}
                  placeholder="0"
                  disabled={isReadOnly}
                />
                <FormInput
                  label="Monthly Expenses"
                  name="expensesMonthly"
                  type="number"
                  value={form.expensesMonthly}
                  onChange={onChange}
                  placeholder="0"
                  disabled={isReadOnly}
                />
              </div>
            </section>
          ) : null}

          {activeStep === 4 ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div className={styles.sectionTitle}>Consent</div>
              </div>

              <label className={styles.checkboxRow}>
                <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} disabled={isReadOnly} />
                <span>I confirm that the information provided is accurate.</span>
              </label>
            </section>
          ) : null}

          <div className={styles.actions}>
            <Button
              variant="primary"
              className={styles.actionBtn}
              onClick={goBack}
              disabled={!canGoBack}
              type="button"
            >
              Back
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button variant="approve" className={styles.actionBtn} onClick={goNext} type="button">
                Next
              </Button>
            ) : (
              <Button variant="approve" className={styles.actionBtn} type="submit">
                Submit
              </Button>
            )}
          </div>
        </form>

        {canDownloadForVetting && activeStep === 0 ? (
          <div className={styles.actions} style={{ justifyContent: "center", marginTop: "1rem" }}>
            <Button variant="approve" type="button" onClick={handleDownloadForVetting}>
              Download for Vetting
            </Button>
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};

export default CreditApplication;
